import { Button, Dialog, DialogContent, Grid } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

import { ButtonLoading } from "src/components/ButtonLoading";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { Form, Formik } from "formik";
import { DropzoneHeader } from "src/components/DropzoneHeader";
import { FileWithPath } from "react-dropzone";
import { DropzoneSingleFileRequirementError } from "src/errors";
import { fieldToError } from "src/utils/form.utils";
import { ButtonGrid } from "src/components/form-components/ButtonGrid";
import { SUPPORTED_FORMATS, useRZoneRestristionsImportSchema } from "src/schemas/residential-zone-restrictions.schema";
import { Dropzone } from "src/components/Dropzone";
import {
  RZRestrictionsImportMutationData,
  useRZRestrictionsImportMutation,
} from "src/hooks/useRZRestrictionsImportMutation";
import { useRZRestrictionsWeightsTemplate } from "src/hooks/useRZRestrictionsWeightsTemplate";
import { ResidentialZoneWeightItem } from "src/types";
import { useState } from "react";

interface Props {
  residentialZoneId: number | string;
  setUploadWeightList: (weightList: ResidentialZoneWeightItem[]) => void;
  incrementUpload: () => void;
  open: boolean;
  readOnly?: boolean;
  onClose: () => void;
}

export function ResidentialZoneRestrictionsUploadDialog(props: Props) {
  const { residentialZoneId, setUploadWeightList, open, readOnly, onClose, incrementUpload } = props;
  const validationSchema = useRZoneRestristionsImportSchema();
  const { data: templates } = useRZRestrictionsWeightsTemplate(residentialZoneId);
  const { mutateAsync, error, isLoading } = useRZRestrictionsImportMutation(residentialZoneId);
  const { t } = useTranslation();
  const [displayBackendError, setDisplayBackendError] = useState(false);

  async function handleSave(importFile: RZRestrictionsImportMutationData) {
    if (!importFile.weightsFile) {
      console.warn("No 'residentialZoneRestrictionFile' field submitted, this should be fixed.");
      return;
    }
    setDisplayBackendError(true);
    const response = await mutateAsync(importFile);
    const { data } = response;
    setUploadWeightList(data.weightList);
    incrementUpload();
    onClose();
  }

  const initialValues: RZRestrictionsImportMutationData = {
    weightsFile: null,
  };

  if (!templates) return null;

  return (
    <StyledDialog fullWidth maxWidth="sm" open={open}>
      <DialogContent>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSave}
          enableReinitialize
        >
          {({ isSubmitting, setFieldValue, errors }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <DropzoneHeader label={t("label.weightsFile")} template={templates.residentialTemplateUrl} />
                  <Dropzone
                    dropzoneOptions={{
                      onDrop: (acceptedFiles: FileWithPath[]) => {
                        if (acceptedFiles.length > 1) throw DropzoneSingleFileRequirementError();
                        setDisplayBackendError(false);
                        setFieldValue("weightsFile", acceptedFiles[0]);
                      },
                      accept: SUPPORTED_FORMATS,
                      multiple: false,
                    }}
                  />

                  {errors.weightsFile && (
                    <Grid item xs={12}>
                      <ServerErrors data={[fieldToError("weightsFileError", errors.weightsFile)]} />
                    </Grid>
                  )}
                </Grid>

                {displayBackendError && error?.response?.data && (
                  <Grid item xs={12}>
                    <ServerErrors data={error?.response?.data} />
                  </Grid>
                )}

                {!readOnly && (
                  <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
                    <Button variant="text" onClick={onClose}>
                      {t("label.close")}
                    </Button>

                    <ButtonLoading
                      isLoading={isSubmitting || isLoading}
                      type="submit"
                      color="primary"
                      variant="contained"
                      // disabled={!(dirty && isValid)}
                    >
                      {t("label.upload")}
                    </ButtonLoading>
                  </ButtonGrid>
                )}
              </Grid>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </StyledDialog>
  );
}

const StyledDialog = styled(Dialog)`
  --text-field-width: 100px;

  .MuiDialogActions-root {
    padding: ${({ theme }) => theme.spacing(3)};
  }

  .MuiDialogContent-root {
    display: flex;
    flex-direction: column;
    overflow: auto;
  }

  .energy-efficiency-measure {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: ${({ theme }) => theme.spacing(2)};
    margin-bottom: ${({ theme }) => theme.spacing(2)};
    background-color: #f7f7f7;
    border: 1px solid #e0e0e0;

    .MuiTypography-root,
    .MuiTextField-root {
      flex-shrink: 0;
    }

    .MuiTextField-root {
      width: var(--text-field-width);
      margin-right: ${({ theme }) => theme.spacing(1)};
    }
    .MuiInputBase-root {
      margin-bottom: 0;
    }
  }
`;
